<template>
  <v-dialog :model-value="modelValue" max-width="480" @update:model-value="$emit('update:modelValue', $event)">
    <v-card class="pa-4">
      <v-card-title class="pa-0 ma-0">
        <span class="text-h5">
          {{ openedKeyState ? 'Regenerate API key' : 'Generate API key' }}
        </span>
      </v-card-title>

      <v-card-text class="pa-0 mt-6">
        <v-select
          v-model="selectedExpiry"
          :items="expiryOptions"
          label="Expiration"
          variant="outlined"
          density="comfortable"
          hide-details
        />

        <div v-if="selectedExpiry === NEVER" class="d-flex align-start mt-3">
          <v-icon size="14" color="warning" class="mr-2 note-icon">mdi-alert-outline</v-icon>
          <span class="text-body-2 text-warning">
            For security, an expiration date is recommended. Without one, this key keeps working until you revoke it.
          </span>
        </div>

        <div v-if="openedKeyState === 'active'" class="d-flex align-start mt-3">
          <v-icon size="14" class="mr-2 note-icon">mdi-information-outline</v-icon>
          <span class="text-body-2 text-medium-emphasis">
            Your current API key stops working. Any MCP client using it loses access within a minute.
          </span>
        </div>
      </v-card-text>

      <v-card-actions class="pa-0 justify-end mt-6">
        <v-btn
          size="large"
          variant="outlined"
          :disabled="loading"
          @click="$emit('update:modelValue', false)"
        >
          Cancel
        </v-btn>
        <v-btn
          size="large"
          variant="flat"
          color="primary"
          :loading="loading"
          @click="onGenerate"
        >
          {{ openedKeyState ? 'Regenerate' : 'Generate' }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { formatApiKeyExpiry } from '@/utils/apiKeyExpiry'

// A v-select reads a null model as "nothing selected", so no expiration travels as a sentinel.
const NEVER = 'never'
const DEFAULT_EXPIRY_DAYS = 90
const MILLISECONDS_PER_DAY = 24 * 60 * 60 * 1000

const EXPIRY_CHOICES = [
  { days: 30, label: '30 days' },
  { days: 90, label: '90 days' },
  { days: 180, label: '6 months' },
  { days: 365, label: '1 year' }
]

const props = defineProps({
  modelValue: { type: Boolean, required: true },
  currentKeyState: { type: String, default: null },
  loading: { type: Boolean, default: false }
})

const emit = defineEmits(['update:modelValue', 'generate'])

const selectedExpiry = ref(DEFAULT_EXPIRY_DAYS)
const openedAt = ref(Date.now())
const openedKeyState = ref(null)

const expiryOptions = computed(() => {
  const dated = EXPIRY_CHOICES.map((choice) => {
    return {
      title: `${choice.label} (${formatApiKeyExpiry(openedAt.value + choice.days * MILLISECONDS_PER_DAY)})`,
      value: choice.days
    }
  })

  return [...dated, { title: 'No expiration', value: NEVER }]
})

const onGenerate = () => {
  emit('generate', selectedExpiry.value === NEVER ? null : selectedExpiry.value)
}

watch(() => props.modelValue, (isOpen) => {
  if (isOpen) {
    selectedExpiry.value = DEFAULT_EXPIRY_DAYS
    openedAt.value = Date.now()
    openedKeyState.value = props.currentKeyState
  }
})
</script>

<style scoped>
.note-icon {
  margin-top: 3px;
}
</style>
